// The kcard skeleton shimmer is stopped globally by kodama-card-loaded.js
// (loaded in theme.liquid) so every surface clears it, not just this page.

class CollectionFilter extends HTMLElement {
  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('.cat-card'));
    this.cardsRow = this.querySelector('.collection-filter__cards');
    this.belt = this.querySelector('.collection-filter__belt');
    this.grid = this.querySelector('.collection-filter__grid');
    this.empty = this.querySelector('.collection-filter__empty');
    if (this.chips.length === 0 || !this.grid) return;

    this.items = Array.from(this.grid.querySelectorAll('.collection-filter__item'));
    this.activeCat = 'all';
    this.remainingLoaded = false;
    this.remainingLoading = null;

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.select(chip));
    });

    // Edge-aware fade: toggle .at-start/.at-end on the belt so the pseudo-element
    // fade only shows on the side that still has content to scroll toward.
    // (Pseudo-element fades instead of a mask-image, which clipped card shadows.)
    if (this.cardsRow && this.belt) {
      this.updateFade();
      this.cardsRow.addEventListener('scroll', () => this.updateFade(), { passive: true });
      window.addEventListener('resize', () => this.updateFade());
    }

    // Catalog view (customize wand): 'sections' regroups the single filtered
    // grid into one section per category. The wand's inline script runs
    // before this deferred one, so check the attribute for the persisted
    // choice AND listen for live toggles.
    this.sectionsBuilt = false;
    this.sectionsWrap = null;
    document.addEventListener('kodama:view', (event) => {
      if (event.detail === 'sections') this.buildSections();
      else this.teardownSections();
    });
    if (document.documentElement.getAttribute('data-kodama-view') === 'sections') {
      this.buildSections();
    }
  }

  // Belt slugs are short demo names; the real store's collection handles are
  // longer (e.g. magic -> magic-the-gathering). Links must use the handle.
  handleFor(cat) {
    const chip = this.chips.find((c) => c.dataset.cat === cat);
    return (chip && chip.dataset.handle) || cat;
  }

  // Label shown as a section heading - reuse the belt cards' localized text.
  labelFor(cat) {
    const label =
      this.querySelector(`.cat-card--iconalt[data-cat="${cat}"] .cat-card__label`) ||
      this.querySelector(`.cat-card[data-cat="${cat}"] .cat-card__label`);
    if (label) return label.textContent.trim();
    const imageCard = this.querySelector(`.cat-card[data-cat="${cat}"][aria-label]`);
    return imageCard ? imageCard.getAttribute('aria-label') : cat;
  }

  async buildSections() {
    if (this.sectionsBuilt) return;

    // Group the products already in the DOM (the first page) right away so the
    // ungrouped showcase is never shown - the raw grid is hidden until now via
    // the is-prebuild guard in the section. Building synchronously means the
    // shopper sees the sections, not the mixed grid, from the first frame JS
    // paints.
    this.renderSections();
    this.sectionsBuilt = true;
    // The items have moved into the section grids; drop the pre-build hide on
    // the now-empty original grid (harmless if the failsafe already cleared it).
    this.grid.classList.remove('is-prebuild');
    document.dispatchEvent(new CustomEvent('kodama:sections'));

    // Then pull the rest of the catalog in the background and fold it into the
    // sections with a single wrap swap - no ungrouped flash, and categories
    // whose products all sit past the first page still get their section.
    this.grid.setAttribute('aria-busy', 'true');
    await this.loadRemaining();
    this.grid.removeAttribute('aria-busy');
    if (!this.sectionsBuilt) return; // toggled back to the filters view meanwhile
    this.renderSections();
    document.dispatchEvent(new CustomEvent('kodama:sections'));
  }

  // Build one section per belt category from the items currently loaded and
  // swap it into place in a single DOM op (insert first time, replace after).
  renderSections() {
    // The belt acts as quick-nav in this view - clear any filter it left behind.
    this.activeCat = 'all';
    this.items.forEach((item) => item.classList.remove('is-hidden'));

    // Belt order = section order (Coleccionables Japón first, Gachapon second,
    // then the rest). No trailing "Todos" section: products that match no belt
    // category stay off the home showcase (still in /collections/all).
    const order = [...new Set(this.chips.map((c) => c.dataset.cat).filter((c) => c && c !== 'all'))];

    // Each item joins exactly ONE section. Preorders claim theirs first:
    // preventa products also belong to their brand's collection, and the brands
    // (earlier in belt order) would otherwise absorb them all, leaving the
    // Preventas section permanently empty. Only when the belt actually shows
    // Preventas though - claiming for a section that never renders would swallow
    // those items entirely.
    const claimOrder = order.includes('preventas')
      ? ['preventas'].concat(order.filter((cat) => cat !== 'preventas'))
      : order;
    const remaining = new Set(this.items);
    const claimed = {};
    claimOrder.forEach((cat) => {
      const members = [...remaining].filter(
        (item) => cat === 'all' || (item.dataset.categories || '').split(/\s+/).includes(cat)
      );
      if (members.length === 0) return;
      members.forEach((item) => remaining.delete(item));
      claimed[cat] = members;
    });

    // Unclaimed items would linger as stray cards in the original grid - hide.
    remaining.forEach((item) => item.classList.add('is-hidden'));

    const wrap = document.createElement('div');
    wrap.className = 'collection-sections';
    order.forEach((cat) => {
      const members = claimed[cat];
      if (!members) return;
      // Newest first: the row cap shows only ~8 items per section, so the
      // NUEVO/recent arrivals must be the ones that surface.
      members.sort(
        (a, b) => (parseInt(b.dataset.created, 10) || 0) - (parseInt(a.dataset.created, 10) || 0)
      );
      const section = document.createElement('section');
      section.className = 'collection-sections__section';
      const heading = document.createElement('h3');
      heading.className = 'collection-sections__heading';
      if (cat === 'all') {
        heading.textContent = this.labelFor(cat);
      } else {
        // Heading links to the category's real collection page - it also acts
        // as the "see everything" affordance for the row-capped grid below.
        const viewall = this.querySelector('[data-viewall]');
        const base = (viewall && viewall.dataset.collectionsBase) || '/collections';
        const link = document.createElement('a');
        link.className = 'collection-sections__link';
        link.href = `${base}/${this.handleFor(cat)}`;
        const label = document.createElement('span');
        label.textContent = this.labelFor(cat);
        const chevron = document.createElement('span');
        chevron.className = 'collection-sections__chevron';
        chevron.textContent = '›';
        chevron.setAttribute('aria-hidden', 'true');
        link.append(label, chevron);
        heading.appendChild(link);
      }
      const grid = document.createElement('div');
      grid.className = 'collection-filter__grid collection-sections__grid';
      members.forEach((item) => {
        grid.appendChild(item);
      });
      section.appendChild(heading);
      section.appendChild(grid);
      wrap.appendChild(section);
    });

    // One atomic swap: replace the previous wrap (background refold) or insert
    // it the first time. appendChild above already moved the item nodes out of
    // whatever grid held them, so the old wrap is safe to drop.
    if (this.sectionsWrap) {
      this.sectionsWrap.replaceWith(wrap);
    } else {
      this.grid.after(wrap);
    }
    this.sectionsWrap = wrap;
  }

  teardownSections() {
    if (!this.sectionsBuilt) return;
    // this.items preserves the original order - re-appending restores it.
    this.items.forEach((item) => this.grid.appendChild(item));
    if (this.sectionsWrap) this.sectionsWrap.remove();
    this.sectionsWrap = null;
    this.sectionsBuilt = false;
    this.applyFilter();
    document.dispatchEvent(new CustomEvent('kodama:sections'));
  }

  updateFade() {
    const el = this.cardsRow;
    const atStart = el.scrollLeft <= 1;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    this.belt.classList.toggle('at-start', atStart);
    this.belt.classList.toggle('at-end', atEnd);
  }

  async select(chip) {
    // Sections view keeps the belt as a quick-nav: a card jumps straight to
    // its category's collection page instead of filtering in place.
    if (document.documentElement.getAttribute('data-kodama-view') === 'sections') {
      const viewall = this.querySelector('[data-viewall]');
      const base = (viewall && viewall.dataset.collectionsBase) || '/collections';
      const cat = chip.dataset.cat;
      window.location.href = cat === 'all' ? `${base}/all` : `${base}/${this.handleFor(cat)}`;
      return;
    }

    this.activeCat = chip.dataset.cat;

    // Category pages exist as real collections (handle == belt slug): show a
    // "view the full category page" link whenever a category is active.
    const viewall = this.querySelector('[data-viewall]');
    if (viewall) {
      if (this.activeCat === 'all') {
        viewall.hidden = true;
      } else {
        viewall.href = `${viewall.dataset.collectionsBase}/${this.handleFor(this.activeCat)}`;
        viewall.hidden = false;
      }
    }

    this.chips.forEach((c) => {
      // Match by category, not identity: image cards have hidden icon twins
      // (customize wand's "icons only" style) that must stay in sync.
      const active = c.dataset.cat === chip.dataset.cat;
      c.classList.toggle('is-active', active);
      c.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    // Lazily pull the rest of the catalog the first time the visitor filters,
    // so the homepage stays light (first 50 products) until they actually need
    // to search a category across everything.
    if (this.activeCat !== 'all' && !this.remainingLoaded) {
      this.grid.setAttribute('aria-busy', 'true');
      await this.loadRemaining();
      this.grid.removeAttribute('aria-busy');
    }

    this.applyFilter();
  }

  applyFilter() {
    const cat = this.activeCat;
    let visible = 0;
    this.items.forEach((item) => {
      const cats = (item.dataset.categories || '').split(/\s+/);
      const show = cat === 'all' || cats.includes(cat);
      item.classList.toggle('is-hidden', !show);
      if (show) visible += 1;
    });
    if (this.empty) this.empty.hidden = visible !== 0;
  }

  loadRemaining() {
    if (this.remainingLoading) return this.remainingLoading;

    const baseUrl = this.grid.dataset.loadMoreUrl;
    const totalPages = parseInt(this.grid.dataset.totalPages || '1', 10);
    if (!baseUrl || totalPages <= 1) {
      this.remainingLoaded = true;
      return Promise.resolve();
    }

    this.remainingLoading = (async () => {
      // The real catalog spans ~24 pages; fetching them one-by-one kept the
      // sections view spinning for tens of seconds. Fetch in parallel and
      // append in page order once they all resolve.
      const pages = [];
      for (let page = 2; page <= totalPages; page += 1) pages.push(page);
      const results = await Promise.all(
        pages.map((page) =>
          fetch(`${baseUrl}&page=${page}`)
            .then((res) => (res.ok ? res.text() : ''))
            .catch(() => '')
        )
      );
      results.forEach((html) => {
        if (!html) return;
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        const newItems = Array.from(tmp.querySelectorAll('.collection-filter__item'));
        newItems.forEach((item) => this.grid.appendChild(item));
        this.items = this.items.concat(newItems);
      });
      this.remainingLoaded = true;
      // Wire up slideshows on the newly appended cards.
      if (typeof window.kodamaSweepCards === 'function') window.kodamaSweepCards();
    })();

    return this.remainingLoading;
  }
}

customElements.define('collection-filter', CollectionFilter);
